import { Directive, HostListener, inject } from '@angular/core';
import { Router } from '@angular/router';

/**
 * Directive to intercept clicks on internal links and use Angular router
 * instead of native navigation (which causes full page reload).
 *
 * Usage:
 *   <div internalLinkRouting [innerHTML]="content | markdown"></div>
 *
 * This directive listens for clicks on <a> tags with class "internal-link"
 * and navigates using Angular's router instead of browser navigation.
 */
@Directive({
  selector: '[internalLinkRouting]',
  standalone: true
})
export class InternalLinkRoutingDirective {
  private router = inject(Router);

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Check if click is on or within any anchor tag
    const anchor = target.closest('a') as HTMLAnchorElement;

    if (anchor) {
      const href = anchor.getAttribute('href');

      if (href && this.isInternalLink(href)) {
        // Prevent default navigation
        event.preventDefault();
        event.stopPropagation();

        // Use Angular router for navigation
        this.router.navigateByUrl(href);
      }
    }
  }

  private isInternalLink(href: string): boolean {
    if (!href) return false;
    // Relative paths and anchors are internal
    if (href.startsWith('/') || href.startsWith('#')) return true;
    // Check if link is to current domain
    try {
      const linkUrl = new URL(href, window.location.href);
      return linkUrl.origin === window.location.origin;
    } catch {
      return false;
    }
  }
}
